/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { isRouteErrorResponse, Link, useRouteError } from "react-router-dom";
import { useAppSelector } from "store";
import { SvgUndo } from "@itwin/itwinui-icons-react";
import { Svg401, SvgError } from "@itwin/itwinui-illustrations-react";
import { Button, NonIdealState } from "@itwin/itwinui-react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

export const RootErrorBoundary = () => {
  const routeError = useRouteError();
  const appInsights = useAppInsightsContext();
  const sandbox = useAppSelector((state) => state.activeSandbox.metadata);
  const action = useAppSelector((state) => state.actions.action)

  if (isRouteErrorResponse(routeError)) {
    if (routeError.status === 404) {
      return <NonIdealState
        svg={<SvgError />}
        heading="Page not found"
        description={<>Return to <Link className="iui-anchor" to="/">home page</Link></>}
      />
    }

    if (routeError.status === 401) {
      return <NonIdealState
        svg={<Svg401 />}
        heading="You are not authorized to access this"
        description={<>Return to <Link className="iui-anchor" to="/">home page</Link></>}
      />
    }
  }

  const error = routeError as Error;

  const trackingProperties = !sandbox ? undefined : {
    "Sandbox user": sandbox.username,
    "Sandbox name": sandbox.name,
    "Sandbox userid": sandbox.userId,
    "Sandbox id": sandbox.id,
    "action": action?.type
  };

  appInsights.trackException({ exception: error }, trackingProperties);

  return <NonIdealState
    svg={<SvgError />}
    heading="Unexpected error occured"
    description={<pre>{error.message || JSON.stringify(error.message)}</pre>}
    actions={<>
      <Button startIcon={<SvgUndo />} onClick={() => (window.location.href = "/")}>Go to the main view</Button>
    </>
    }
  />
};
